/**
 * snapUtils.js - Shared snapping utilities for all drawing tools
 *
 * Provides hierarchical snapping functionality:
 * 1. Endpoint snap (highest priority) - snap to corners, midpoints, vertices of other objects
 * 2. Grid snap (if enabled and Ctrl not pressed)
 * 3. Raw coordinates (no snap)
 *
 * Usage:
 * ```javascript
 * import { applyHierarchicalSnap, ENDPOINT_SNAP_DISTANCE } from './snapUtils';
 *
 * const snapResult = applyHierarchicalSnap(
 *   pointer,
 *   canvas,
 *   gridSize,
 *   snapEnabled,
 *   ctrlPressed,
 *   ENDPOINT_SNAP_DISTANCE
 * );
 * ```
 */

/**
 * Default snap distance constant (in pixels)
 * Used for endpoint snapping across all tools
 */
export const ENDPOINT_SNAP_DISTANCE = 10;

/**
 * Calculate Euclidean distance between two points
 *
 * @param {object} p1 - First point {x, y}
 * @param {object} p2 - Second point {x, y}
 * @returns {number} Distance in pixels
 */
export const calculateDistance = (p1, p2) => {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Check if a point is near a target point within tolerance
 *
 * @param {object} point - Point to check {x, y}
 * @param {object} target - Target point {x, y}
 * @param {number} tolerance - Maximum distance (default: 10)
 * @returns {boolean} True if point is within tolerance of target
 */
export const isNearPoint = (point, target, tolerance = 10) => {
  return calculateDistance(point, target) <= tolerance;
};

/**
 * Apply snap-to-grid to a single value
 *
 * @param {number} value - Value to snap
 * @param {number} gridSize - Grid size in pixels
 * @param {boolean} snapEnabled - Whether snap is enabled
 * @param {boolean} ctrlPressed - Whether Ctrl key is pressed (disables snap)
 * @returns {number} Snapped value
 */
export const snapValue = (value, gridSize, snapEnabled, ctrlPressed) => {
  if (!snapEnabled || ctrlPressed) {
    return value;
  }
  return Math.round(value / gridSize) * gridSize;
};

/**
 * Get endpoints/snap points for different Fabric.js object types
 *
 * Returns snap-worthy points based on object type:
 * - Rectangle: 4 corners + 4 midpoints = 8 points
 * - Circle: center + 4 cardinal points = 5 points
 * - Line: 2 endpoints + midpoint = 3 points
 * - Polygon/Polyline: all vertices
 * - Other: bounding box corners
 *
 * @param {fabric.Object} obj - Fabric.js object
 * @returns {Array<{x: number, y: number}>} Array of snap points in canvas coordinates
 */
export const getObjectEndpoints = (obj) => {
  if (!obj || obj.data?.isBackground || obj.data?.isGardenBoundary || obj.data?.isTemporary) {
    return []; // Skip background, boundary, and temporary objects
  }

  // Helper to transform local coordinates to canvas coordinates
  const transformPoint = (localX, localY) => {
    // Get object's transform matrix
    const matrix = obj.calcTransformMatrix();

    // Transform the point
    const point = {
      x: localX,
      y: localY
    };

    // Apply transformation using Fabric's utility
    const transformed = {
      x: matrix[0] * point.x + matrix[2] * point.y + matrix[4],
      y: matrix[1] * point.x + matrix[3] * point.y + matrix[5]
    };

    return transformed;
  };

  switch (obj.type) {
    case 'rect': {
      // 4 corners + 4 midpoints = 8 snap points
      const w = obj.width || 0;
      const h = obj.height || 0;
      return [
        transformPoint(0, 0),           // Top-left
        transformPoint(w / 2, 0),       // Top-middle
        transformPoint(w, 0),           // Top-right
        transformPoint(w, h / 2),       // Right-middle
        transformPoint(w, h),           // Bottom-right
        transformPoint(w / 2, h),       // Bottom-middle
        transformPoint(0, h),           // Bottom-left
        transformPoint(0, h / 2),       // Left-middle
      ];
    }

    case 'circle': {
      // Center + 4 cardinal points
      const r = obj.radius || 0;
      return [
        transformPoint(r, r),           // Center
        transformPoint(r, 0),           // Top
        transformPoint(r * 2, r),       // Right
        transformPoint(r, r * 2),       // Bottom
        transformPoint(0, r),           // Left
      ];
    }

    case 'line': {
      // Both endpoints + midpoint
      const x1 = obj.x1 || 0;
      const y1 = obj.y1 || 0;
      const x2 = obj.x2 || 0;
      const y2 = obj.y2 || 0;

      // Lines use different coordinate system (absolute canvas coords)
      return [
        { x: obj.left + x1, y: obj.top + y1 },                    // Start
        { x: obj.left + (x1 + x2) / 2, y: obj.top + (y1 + y2) / 2 }, // Midpoint
        { x: obj.left + x2, y: obj.top + y2 },                    // End
      ];
    }

    case 'polygon':
    case 'polyline': {
      // All vertices
      const points = obj.points || [];
      return points.map(p => transformPoint(p.x, p.y));
    }

    default: {
      // Fallback: bounding box corners
      const bound = obj.getBoundingRect();
      return [
        { x: bound.left, y: bound.top },
        { x: bound.left + bound.width, y: bound.top },
        { x: bound.left + bound.width, y: bound.top + bound.height },
        { x: bound.left, y: bound.top + bound.height },
      ];
    }
  }
};

/**
 * Snap point to nearest object endpoint if within tolerance
 *
 * Searches all canvas objects for snap points and returns the nearest one
 * within the specified snap distance.
 *
 * @param {object} point - {x, y} coordinates to snap
 * @param {fabric.Canvas} canvas - Fabric.js canvas instance
 * @param {number} snapDistance - Maximum snap distance in pixels (default: 10)
 * @param {(obj: fabric.Object) => boolean} [shouldSkip] - Optional predicate; when it
 *   returns true for an object, that object is not considered as a snap source.
 *   Used by vertex editing to exclude the object being edited and its transient
 *   handles — otherwise the dragged handle snaps to its own center (distance 0).
 * @returns {object} - {point: {x, y}, snapped: boolean, target: object|null}
 */
export const snapToNearestEndpoint = (point, canvas, snapDistance = ENDPOINT_SNAP_DISTANCE, shouldSkip = null) => {
  if (!canvas) {
    return { point, snapped: false, target: null };
  }

  const objects = canvas.getObjects();
  let nearestPoint = null;
  let nearestTarget = null;
  let minDistance = snapDistance;

  objects.forEach(obj => {
    // Skip selection boxes and temporary objects
    if (obj.excludeFromExport || obj.data?.isTemporary) {
      return;
    }

    // Caller-supplied exclusion (e.g. the edited path and its vertex handles)
    if (shouldSkip && shouldSkip(obj)) {
      return;
    }

    const endpoints = getObjectEndpoints(obj);
    endpoints.forEach(endpoint => {
      const distance = calculateDistance(point, endpoint);
      if (distance < minDistance) {
        minDistance = distance;
        nearestPoint = endpoint;
        nearestTarget = obj;
      }
    });
  });

  if (nearestPoint) {
    return {
      point: nearestPoint,
      snapped: true,
      target: nearestTarget,
    };
  }

  return { point, snapped: false, target: null };
};

/**
 * Apply hierarchical snapping: endpoint → grid → raw
 *
 * This is the main snapping function that should be used by all drawing tools.
 * It implements a priority-based snapping system:
 *
 * 1. **Endpoint Snap** (highest priority): If the mouse is within `endpointSnapDistance`
 *    of any object's snap point (corner, midpoint, vertex), snap to it.
 *
 * 2. **Grid Snap** (medium priority): If endpoint snap didn't trigger, and grid snapping
 *    is enabled, and Ctrl is not pressed, snap to grid.
 *
 * 3. **Raw Coordinates** (no snap): If neither endpoint nor grid snap applies, use
 *    raw mouse coordinates.
 *
 * @param {object} rawPoint - {x, y} raw mouse coordinates
 * @param {fabric.Canvas} canvas - Canvas instance for endpoint detection
 * @param {number} gridSize - Grid size for snap-to-grid
 * @param {boolean} gridSnapEnabled - Whether grid snapping is enabled
 * @param {boolean} ctrlPressed - Whether Ctrl key is pressed (disables grid snap)
 * @param {number} endpointSnapDistance - Endpoint snap tolerance (default: ENDPOINT_SNAP_DISTANCE)
 * @param {(obj: fabric.Object) => boolean} [shouldSkip] - Optional endpoint-source
 *   exclusion predicate, forwarded to snapToNearestEndpoint.
 * @returns {object} - {point: {x, y}, snappedTo: 'endpoint'|'grid'|'none', target: object|null}
 *
 * @example
 * const snapResult = applyHierarchicalSnap(
 *   { x: 105, y: 205 },
 *   canvas,
 *   10,
 *   true,
 *   false,
 *   ENDPOINT_SNAP_DISTANCE
 * );
 *
 * console.log(snapResult.point); // { x: 100, y: 200 } (snapped to endpoint)
 * console.log(snapResult.snappedTo); // 'endpoint'
 * console.log(snapResult.target); // Rectangle object
 */
export const applyHierarchicalSnap = (
  rawPoint,
  canvas,
  gridSize,
  gridSnapEnabled,
  ctrlPressed,
  endpointSnapDistance = ENDPOINT_SNAP_DISTANCE,
  shouldSkip = null
) => {
  // Priority 1: Snap to endpoint (always, if within range)
  const endpointSnap = snapToNearestEndpoint(rawPoint, canvas, endpointSnapDistance, shouldSkip);
  if (endpointSnap.snapped) {
    return {
      point: endpointSnap.point,
      snappedTo: 'endpoint',
      target: endpointSnap.target,
    };
  }

  // Priority 2: Snap to grid (if enabled and Ctrl not pressed)
  if (gridSnapEnabled && !ctrlPressed) {
    return {
      point: {
        x: snapValue(rawPoint.x, gridSize, true, false),
        y: snapValue(rawPoint.y, gridSize, true, false),
      },
      snappedTo: 'grid',
      target: null,
    };
  }

  // Priority 3: No snap (raw coordinates)
  return {
    point: rawPoint,
    snappedTo: 'none',
    target: null,
  };
};
